import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Paper,
  Group,
  Text,
  SimpleGrid,
  Card,
  Badge,
  Button,
  Skeleton,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconLeaf,
  IconSun,
  IconClock,
  IconShoppingCartPlus,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useAuthStore } from '@/store/auth';
import { useSeasonalRecs, useAddToCart } from '@/hooks/api';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function SeasonalPicks() {
  const navigate = useNavigate();
  const customerId = useAuthStore((s) => s.user?.customer?.id);
  const { data: seasonal, isLoading } = useSeasonalRecs(customerId);
  const addToCart = useAddToCart();

  const handleAddItem = async (productId: number, productName: string) => {
    try {
      await addToCart.mutateAsync({ product_id: productId, quantity: 1 });
      notifications.show({
        title: 'Added to Cart',
        message: `${productName} added to cart`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add item to cart',
        color: 'red',
      });
    }
  };

  const currentMonth = seasonal?.month || new Date().getMonth() + 1;
  const seasonLabel = seasonal?.season || '';

  return (
    <Stack gap="lg">
      <PageHeader
        title="Seasonal Picks"
        description="Fresh, in-season products for your menu"
        actions={
          <Button variant="subtle" onClick={() => navigate('/shop/seasonal')}>
            Full Seasonal Calendar
          </Button>
        }
      />

      {/* Season Badge */}
      <Paper p="lg" radius="md" withBorder>
        <Group gap="md">
          <ThemeIcon size={48} radius="xl" color="green" variant="light">
            <IconLeaf size={24} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {MONTH_NAMES[currentMonth]} {seasonLabel && `\u2014 ${seasonLabel}`}
            </Text>
            <Text size="sm" c="dimmed">
              Products curated for what's at peak quality right now
            </Text>
          </div>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack gap="md">
          <Skeleton height={200} radius="md" />
          <Skeleton height={200} radius="md" />
        </Stack>
      ) : !seasonal ? (
        <Paper p="lg" radius="md" withBorder>
          <EmptyState
            title="No Seasonal Data"
            description="Seasonal recommendations are not available at this time."
            icon={<IconLeaf size={48} />}
          />
        </Paper>
      ) : (
        <>
          {/* In Season Now */}
          {seasonal.in_season && seasonal.in_season.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group gap="xs">
                  <IconSun size={20} color="var(--mantine-color-green-6)" />
                  <Text fw={600} size="lg">In Season Now</Text>
                  <Badge size="sm" color="green" variant="light">
                    {seasonal.in_season.length} products
                  </Badge>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {seasonal.in_season.map((item) => (
                    <Card key={item.product_id} padding="md" radius="md" withBorder>
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text fw={500} size="sm">{item.product_name}</Text>
                          {item.peak && (
                            <Badge size="xs" color="green">Peak</Badge>
                          )}
                        </Group>
                        {item.category && (
                          <Text size="xs" c="dimmed">{item.category}</Text>
                        )}
                        <Group justify="space-between" mt="xs">
                          <Text fw={600}>${item.unit_price.toFixed(2)}</Text>
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconShoppingCartPlus size={14} />}
                            onClick={() => handleAddItem(item.product_id, item.product_name)}
                            loading={addToCart.isPending}
                          >
                            Add
                          </Button>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>
          )}

          {/* Coming Soon */}
          {seasonal.coming_soon && seasonal.coming_soon.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group gap="xs">
                  <IconClock size={20} color="var(--mantine-color-blue-6)" />
                  <Text fw={600} size="lg">Coming Soon</Text>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {seasonal.coming_soon.map((item) => (
                    <Card key={item.product_id} padding="md" radius="md" withBorder>
                      <Group justify="space-between">
                        <Text fw={500} size="sm">{item.product_name}</Text>
                        <Badge size="sm" variant="light" color="blue">
                          {MONTH_NAMES[item.available_month]}
                        </Badge>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>
          )}

          {/* Ending Soon */}
          {seasonal.ending_soon && seasonal.ending_soon.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group gap="xs">
                  <IconClock size={20} color="var(--mantine-color-orange-6)" />
                  <Text fw={600} size="lg">Ending Soon</Text>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {seasonal.ending_soon.map((item) => (
                    <Card key={item.product_id} padding="md" radius="md" withBorder>
                      <Group justify="space-between">
                        <Text fw={500} size="sm">{item.product_name}</Text>
                        <Badge size="sm" variant="light" color="orange">
                          Until {MONTH_NAMES[item.last_month]}
                        </Badge>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
